export default function InvestmentFocus() {
  return (
    <section id="investment-focus" className="py-16 md:py-[110px]">
      <div className="max-w-[1120px] mx-auto px-5 md:px-10">
        <h2 className="text-[24px] md:text-[30px] font-semibold text-[#1E2430] mb-[14px]">
          Investment Thesis
        </h2>
        
        <div className="max-w-[720px]">
          <h3 className="text-[22px] font-semibold text-[#1E2430] mb-[12px]">
            Building the Next Exceptional Platform
          </h3>
          
          <p className="text-[17px] leading-[1.65] text-[#5B6475] mb-6">
            Valaxis Group is a CEO-led acquisition initiative focused on acquiring and operating a high-quality healthcare or technology-enabled services business. We seek to partner with long-term investors to acquire a profitable company and build a scaled platform through operational leadership, strategic investment, and disciplined growth.
          </p>
          
          <p className="text-[17px] leading-[1.65] text-[#5B6475] mb-12">
            Our approach combines hands-on executive leadership with the resources of aligned capital partners to create enduring value over the long term.
          </p>
        </div>

        {/* Sector Focus */}
        <div className="mt-[48px]">
          <h3 className="text-[24px] font-semibold text-[#1E2430] mb-[14px]">
            Sector Focus
          </h3>
          
          <p className="text-[17px] leading-[1.65] text-[#5B6475] max-w-[720px] mb-6">
            We focus on businesses operating in sectors where regulation, complexity, and mission-critical services create durable demand and high customer retention.
          </p>
          
          <p className="text-[17px] leading-[1.65] text-[#5B6475] max-w-[720px] mb-4">
            Primary areas of interest include:
          </p>
          
          <ul className="space-y-3 mb-6 max-w-[720px]">
            <li className="flex items-start gap-3">
              <span className="text-[#2E3A59] mt-1">•</span>
              <span className="text-[17px] leading-[1.65] text-[#5B6475]">
                Healthcare Services
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#2E3A59] mt-1">•</span>
              <span className="text-[17px] leading-[1.65] text-[#5B6475]">
                Technology-Enabled Services
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#2E3A59] mt-1">•</span>
              <span className="text-[17px] leading-[1.65] text-[#5B6475]">
                Regulatory & Compliance Services
              </span>
            </li>
          </ul>
          
          <p className="text-[17px] leading-[1.65] text-[#5B6475] max-w-[720px]">
            These sectors benefit from long-term structural growth, fragmented markets, and opportunities for operational improvement.
          </p>
        </div>

        {/* Target Company Profile */}
        <div className="mt-[48px]">
          <h3 className="text-[24px] font-semibold text-[#1E2430] mb-[14px]">
            Target Company Profile
          </h3>
          
          <p className="text-[17px] leading-[1.65] text-[#5B6475] max-w-[720px] mb-6">
            We seek to acquire businesses with the following characteristics:
          </p>
          
          <ul className="space-y-3 mb-6 max-w-[720px]">
            <li className="flex items-start gap-3">
              <span className="text-[#2E3A59] mt-1">•</span>
              <span className="text-[17px] leading-[1.65] text-[#5B6475]">
                $2M – $10M EBITDA
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#2E3A59] mt-1">•</span>
              <span className="text-[17px] leading-[1.65] text-[#5B6475]">
                Recurring or contracted revenue models
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#2E3A59] mt-1">•</span>
              <span className="text-[17px] leading-[1.65] text-[#5B6475]">
                Strong customer retention and mission-critical offerings
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#2E3A59] mt-1">•</span>
              <span className="text-[17px] leading-[1.65] text-[#5B6475]">
                Fragmented competitive landscapes
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#2E3A59] mt-1">•</span>
              <span className="text-[17px] leading-[1.65] text-[#5B6475]">
                Opportunities for operational improvement or strategic acquisitions
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-[#2E3A59] mt-1">•</span>
              <span className="text-[17px] leading-[1.65] text-[#5B6475]">
                Founder succession or growth transition
              </span>
            </li>
          </ul>
          
          <p className="text-[17px] leading-[1.65] text-[#5B6475] max-w-[720px]">
            Target enterprise value typically ranges from $15M to $100M.
          </p>
        </div>

        {/* Value Creation Strategy */}
        <div className="mt-10 md:mt-[48px]">
          <h3 className="text-[20px] md:text-[24px] font-semibold text-[#1E2430] mb-[14px]">
            Value Creation Strategy
          </h3>
          
          <p className="text-[16px] md:text-[17px] leading-[1.65] text-[#5B6475] max-w-[720px] mb-6">
            Valaxis Group focuses on long-term value creation through a combination of operational leadership and disciplined growth initiatives.
          </p>
          
          <p className="text-[16px] md:text-[17px] leading-[1.65] text-[#5B6475] max-w-[720px] mb-4">
            Key drivers include:
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-[28px]">
            <div className="bg-white rounded-[14px] p-6 md:p-[36px] border border-[rgba(30,36,48,0.08)]">
              <h4 className="text-[17px] md:text-[19px] font-semibold text-[#1E2430] mb-3">Operational Excellence</h4>
              <p className="text-[15px] md:text-[17px] leading-[1.65] text-[#5B6475]">Implementing best-in-class leadership, systems, and processes to improve scalability and performance.</p>
            </div>
            <div className="bg-white rounded-[14px] p-6 md:p-[36px] border border-[rgba(30,36,48,0.08)]">
              <h4 className="text-[17px] md:text-[19px] font-semibold text-[#1E2430] mb-3">Commercial Acceleration</h4>
              <p className="text-[15px] md:text-[17px] leading-[1.65] text-[#5B6475]">Strengthening sales execution, marketing capabilities, and customer expansion.</p>
            </div>
            <div className="bg-white rounded-[14px] p-6 md:p-[36px] border border-[rgba(30,36,48,0.08)]">
              <h4 className="text-[17px] md:text-[19px] font-semibold text-[#1E2430] mb-3">Strategic Acquisitions</h4>
              <p className="text-[15px] md:text-[17px] leading-[1.65] text-[#5B6475]">Pursuing complementary acquisitions to expand capabilities and market reach.</p>
            </div>
            <div className="bg-white rounded-[14px] p-6 md:p-[36px] border border-[rgba(30,36,48,0.08)]">
              <h4 className="text-[17px] md:text-[19px] font-semibold text-[#1E2430] mb-3">Technology Enablement</h4>
              <p className="text-[15px] md:text-[17px] leading-[1.65] text-[#5B6475]">Leveraging software and data capabilities to enhance efficiency and competitive differentiation.</p>
            </div>
          </div>
        </div>

        {/* Investment Partnership */}
        <div className="mt-[48px]">
          <h3 className="text-[24px] font-semibold text-[#1E2430] mb-[14px]">
            Investment Partnership
          </h3>
          
          <p className="text-[17px] leading-[1.65] text-[#5B6475] max-w-[720px]">
            Valaxis Group seeks to partner with long-term investors who value operator-led value creation and alignment of interests. Investors participate alongside management in building a durable platform designed for sustained growth and long-term value creation.
          </p>
        </div>

        {/* Long-Term Vision */}
        <div className="mt-[48px]">
          <h3 className="text-[24px] font-semibold text-[#1E2430] mb-[14px]">
            Long-Term Vision
          </h3>
          
          <p className="text-[17px] leading-[1.65] text-[#5B6475] max-w-[720px]">
            The objective is to acquire a strong initial platform and build a leading company within its market through disciplined growth, strategic acquisitions, and operational excellence.
          </p>
        </div>
      </div>
    </section>
  );
}